/**
 * SpaceContext — global active space (workspace) selector.
 *
 * The user can belong to multiple spaces (personal + invited team
 * spaces). This context tracks WHICH space is currently active for
 * the whole app: dashboard, movements, wealth, reports, etc.
 *
 * Why localStorage instead of URL state: the active space follows the
 * user across tabs and reloads. Persisting it under `lumfi_active_space`
 * also lets the axios interceptor stamp the `X-Space-Id` header on
 * every request without threading the value through every service call.
 *
 * Switching spaces triggers a FULL SPA reload (per user request).
 * Rationale: every cached query is scoped to the previous space — a
 * soft invalidation would still keep stale derived state, and a full
 * reload is the safest "what you see is from the space you picked"
 * guarantee.
 */
import { createContext, useContext, useState, useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";

const SpaceCtx = createContext(null);

export const ACTIVE_SPACE_KEY = "lumfi_active_space";

function readActiveSpaceId() {
  try { return localStorage.getItem(ACTIVE_SPACE_KEY) || null; } catch { return null; }
}
function writeActiveSpaceId(v) {
  try {
    if (v) localStorage.setItem(ACTIVE_SPACE_KEY, v);
    else localStorage.removeItem(ACTIVE_SPACE_KEY);
  } catch { /* private mode */ }
}

export function SpaceProvider({ children }) {
  const { user } = useAuth();
  // Persisted selection. Only mutated explicitly via switchSpace.
  const [storedSpaceId, setStoredSpaceId] = useState(() => readActiveSpaceId());

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["spaces"],
    queryFn: async () => (await api.get("/spaces")).data,
    enabled: !!user?.id,
    staleTime: 5 * 60_000,
    retry: false,
  });
  const spaces = useMemo(() => data?.items || [], [data]);

  // Derive the EFFECTIVE active space: if the persisted choice is
  // valid (the user still has access), use it; otherwise fall back to
  // the personal space (space.id === user.id) or the first space.
  // We compute instead of useEffect-set to keep the `set-state-in-effect`
  // lint rule happy.
  const activeSpaceId = useMemo(() => {
    if (!spaces.length) return storedSpaceId;
    const ids = new Set(spaces.map((s) => s.id));
    if (storedSpaceId && ids.has(storedSpaceId)) return storedSpaceId;
    const personal = spaces.find((s) => s.id === user?.id);
    return (personal?.id) || spaces[0].id;
  }, [storedSpaceId, spaces, user?.id]);

  // Whenever the derived id diverges from the stored one (first load,
  // stale localStorage pointing at a revoked space), persist it so the
  // axios interceptor uses the same id the UI is showing. We do it via
  // a render-time write rather than useEffect because writing to
  // localStorage is idempotent and cheap.
  if (activeSpaceId && activeSpaceId !== storedSpaceId) {
    writeActiveSpaceId(activeSpaceId);
  }

  // Hard-switch the active space and reload the SPA. Reload is the
  // user's explicit choice to wipe in-memory cache and re-fetch every
  // resource under the new scope.
  const switchSpace = useCallback((nextId, { reload = true } = {}) => {
    if (!nextId || nextId === activeSpaceId) return;
    writeActiveSpaceId(nextId);
    setStoredSpaceId(nextId);
    if (reload && typeof window !== "undefined") {
      window.setTimeout(() => { window.location.reload(); }, 60);
    }
  }, [activeSpaceId]);

  const activeSpace = spaces.find((s) => s.id === activeSpaceId) || null;

  // iter-170am-part5 — Derived flag: can the user add MORE workspaces?
  // We query the user's entitlements once and derive (cap > 0 OR unlimited).
  // If the user has no team-spaces capability AND only their personal
  // space, we hide the workspace UI affordances (switcher, global view,
  // "create space" CTAs) so the SPA looks like a single-workspace app.
  // Cached for 5min, never blocks the layout — falls back to "false"
  // until the request finishes.
  const { data: myPlan } = useQuery({
    queryKey: ["billing-me-spaces"],
    queryFn: async () => (await api.get("/billing/me")).data,
    enabled: !!user?.id,
    staleTime: 5 * 60_000,
    retry: false,
  });
  const spacesEntitlement = (myPlan?.entitlements?.resources || [])
    .find((r) => r?.key === "spaces");
  const canAddMoreSpaces = Boolean(
    spacesEntitlement?.unlimited ||
    (spacesEntitlement?.limit > 0 && (spacesEntitlement?.used || 0) < spacesEntitlement.limit),
  );
  // The UI should show multi-workspace affordances only when the user
  // actually has more than one OR is capable of creating more.
  const isMultiWorkspaceUser = spaces.length > 1 || canAddMoreSpaces;

  const value = {
    activeSpaceId,
    activeSpace,
    spaces,
    isLoading,
    switchSpace,
    refetch,
    canAddMoreSpaces,
    isMultiWorkspaceUser,
  };
  return <SpaceCtx.Provider value={value}>{children}</SpaceCtx.Provider>;
}

export function useSpace() {
  const ctx = useContext(SpaceCtx);
  if (!ctx) throw new Error("useSpace must be used within SpaceProvider");
  return ctx;
}
